import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useState, type FC } from 'react';
import classnames from 'classnames';
import { AdvSponsorshipImage, AdvSponsorshipOverlay, useAdvGalleryPlacement } from '@sbt-web/adv';
import { Carousel as CarouselLibrary } from '@sbt-web/carousel';
import { useViewport } from '@sbt-web/hooks';
import type { AdItem, ItemCategory, ItemImage } from '@sbt-web/network/types';
import CarouselCell from './CarouselCell';
import Placeholder from './Placeholder';
import ownClasses from './index.module.scss';
interface Props {
  images: ItemImage[];
  alt: string;
  category: ItemCategory;
  currentIndex: number;
  onChange: (index: number) => void;
  reduceDataUse?: boolean;
  goToAdv?: boolean;
  setGoToAdv?: (v: boolean) => void;
  ad?: AdItem;
  switchFullscreen?: () => void;
  fullScreen?: boolean;
}

// Safari doesn't support lazy loading yet, so we only use eager loading.
// Otherwise the image only loads when scrolled to and it feels slow.
// iPhones and Macs are powerful devices that will be able to handle
// the extra load.
const isLikelyOnSafari =
// This will render with false on the server,
// and then switch and download the images eagerly only on Safari.
typeof window !== 'undefined' && 'safari' in window;
const Carousel: FC<Props> = t0 => {
  const $ = _c(61);
  const {
    reduceDataUse,
    images,
    alt,
    currentIndex,
    onChange,
    category,
    goToAdv: t1,
    setGoToAdv,
    ad,
    switchFullscreen,
    fullScreen
  } = t0;
  const goToAdv = t1 === undefined ? false : t1;
  const {
    isDesktop
  } = useViewport();
  const {
    advLoaded
  } = useAdvGalleryPlacement();
  const [carouselIndex, setCarouselIndex] = useState(currentIndex);
  const [shouldSkipLazyLoading, setShouldSkipLazyLoading] = useState(false);
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (isLikelyOnSafari) {
        setShouldSkipLazyLoading(true);
      }
    };
    t3 = [];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useEffect(t2, t3);
  const [isMounted, setIsMounted] = useState(false);
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setIsMounted(true);
    };
    t5 = [];
    $[2] = t4;
    $[3] = t5;
  } else {
    t4 = $[2];
    t5 = $[3];
  }
  useEffect(t4, t5);
  let t6;
  if ($[4] !== ad || $[5] !== advLoaded || $[6] !== alt || $[7] !== fullScreen || $[8] !== images || $[9] !== isDesktop || $[10] !== reduceDataUse || $[11] !== shouldSkipLazyLoading || $[12] !== switchFullscreen) {
    let t7;
    if ($[14] !== alt || $[15] !== fullScreen || $[16] !== isDesktop || $[17] !== reduceDataUse || $[18] !== shouldSkipLazyLoading || $[19] !== switchFullscreen) {
      t7 = (image, index) => <CarouselCell key={image.cdnBaseUrl} index={index} alt={alt} image={image} isDesktop={isDesktop} fullScreen={fullScreen} reduceDataUse={reduceDataUse} shouldSkipLazyLoading={shouldSkipLazyLoading} switchFullscreen={switchFullscreen} />;
      $[14] = alt;
      $[15] = fullScreen;
      $[16] = isDesktop;
      $[17] = reduceDataUse;
      $[18] = shouldSkipLazyLoading;
      $[19] = switchFullscreen;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    let t8;
    if ($[21] !== ad || $[22] !== advLoaded || $[23] !== images.length) {
      t8 = <AdvSponsorshipImage key="adv-slide" gallerySizes={images.length} advLoaded={advLoaded} ad={ad} />;
      $[21] = ad;
      $[22] = advLoaded;
      $[23] = images.length;
      $[24] = t8;
    } else {
      t8 = $[24];
    }
    t6 = images.filter(_temp).map(t7).concat(t8);
    $[4] = ad;
    $[5] = advLoaded;
    $[6] = alt;
    $[7] = fullScreen;
    $[8] = images;
    $[9] = isDesktop;
    $[10] = reduceDataUse;
    $[11] = shouldSkipLazyLoading;
    $[12] = switchFullscreen;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const cells = t6;
  let t7;
  if ($[25] !== images.length || $[26] !== onChange || $[27] !== setGoToAdv) {
    t7 = index_0 => {
      if (index_0 < images.length) {
        onChange(index_0);
        if (setGoToAdv) {
          setGoToAdv(false);
        }
      } else {
        if (index_0 == images.length) {
          if (setGoToAdv) {
            setGoToAdv(true);
          }
        }
      }
    };
    $[25] = images.length;
    $[26] = onChange;
    $[27] = setGoToAdv;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  const onChangeSlide = t7;
  let t8;
  let t9;
  if ($[29] !== cells.length || $[30] !== goToAdv) {
    t8 = () => {
      if (goToAdv) {
        setCarouselIndex(cells.length - 1);
      }
    };
    t9 = [cells.length, goToAdv];
    $[29] = cells.length;
    $[30] = goToAdv;
    $[31] = t8;
    $[32] = t9;
  } else {
    t8 = $[31];
    t9 = $[32];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[33] !== currentIndex || $[34] !== goToAdv) {
    t10 = () => {
      if (!goToAdv) {
        setCarouselIndex(currentIndex);
      }
    };
    t11 = [currentIndex, goToAdv];
    $[33] = currentIndex;
    $[34] = goToAdv;
    $[35] = t10;
    $[36] = t11;
  } else {
    t10 = $[35];
    t11 = $[36];
  }
  useEffect(t10, t11);
  if (images.length === 0) {
    let t12;
    if ($[37] !== category.id) {
      t12 = <Placeholder categoryId={category.id} />;
      $[37] = category.id;
      $[38] = t12;
    } else {
      t12 = $[38];
    }
    return t12;
  }
  let t12;
  if ($[39] !== currentIndex || $[40] !== goToAdv || $[41] !== images.length) {
    t12 = goToAdv ? null : <p className={classnames("caption", "small", ownClasses.counter)}>{`${currentIndex + 1}/${images.length}`}</p>;
    $[39] = currentIndex;
    $[40] = goToAdv;
    $[41] = images.length;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  let t13;
  if ($[43] !== fullScreen) {
    t13 = classnames(ownClasses.carousel, {
      [ownClasses["full-screen-carousel"]]: fullScreen
    });
    $[43] = fullScreen;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  let t14;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      right: "Vai alla prossima immagine",
      left: "Vai all'immagine precedente"
    };
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  const t15 = isMounted && (advLoaded || images.length > 1);
  let t16;
  if ($[46] !== advLoaded || $[47] !== goToAdv || $[48] !== images.length) {
    t16 = <AdvSponsorshipOverlay imageArray={images.length} goToAdv={goToAdv} advLoaded={advLoaded} />;
    $[46] = advLoaded;
    $[47] = goToAdv;
    $[48] = images.length;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== carouselIndex || $[51] !== cells || $[52] !== images.length || $[53] !== onChangeSlide || $[54] !== t13 || $[55] !== t15 || $[56] !== t16) {
    t17 = <CarouselLibrary cssClass={t13} labelArrows={t14} listItems={cells} itemCount={images.length} currentIndex={carouselIndex} onChange={onChangeSlide} showButtons={t15} withDots={true} overlayElement={t16} />;
    $[50] = carouselIndex;
    $[51] = cells;
    $[52] = images.length;
    $[53] = onChangeSlide;
    $[54] = t13;
    $[55] = t15;
    $[56] = t16;
    $[57] = t17;
  } else {
    t17 = $[57];
  }
  let t18;
  if ($[58] !== t12 || $[59] !== t17) {
    t18 = <>{t12}{t17}</>;
    $[58] = t12;
    $[59] = t17;
    $[60] = t18;
  } else {
    t18 = $[60];
  }
  return t18;
};
export default Carousel;
function _temp(img) {
  return img?.cdnBaseUrl != null;
}